import { AlertCircle, AlertTriangle, Bell, CheckCircle2, Info, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotifications } from '@/contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

export function NotificationHistory() {
  const { notifications, clearNotifications } = useNotifications();
  const { t } = useTranslation('common');

  // K47: Status als Symbol plus uebersetzter sr-only-Name statt des rohen
  // Enum-Worts ("error"/"success") im Klartext.
  const TYPE_ICON = {
    success: { Icon: CheckCircle2, className: 'text-success' },
    error: { Icon: AlertCircle, className: 'text-destructive' },
    warning: { Icon: AlertTriangle, className: 'text-warning-ink' },
    info: { Icon: Info, className: 'text-primary' },
  } as const;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-11 w-11 sm:h-10 sm:w-10" aria-label={t('notifications.title')}>
          <Bell className="h-5 w-5" />
          {notifications.length > 0 && (
            <span
              data-testid="notification-count"
              className="absolute -top-1 -right-1 h-5 min-w-5 rounded-full bg-primary px-1 text-primary-foreground text-xs flex items-center justify-center"
            >
              {notifications.length > 99 ? '99+' : notifications.length}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle>{t('notifications.title')}</SheetTitle>
              <SheetDescription className={notifications.length === 0 ? 'sr-only' : undefined}>
                {notifications.length === 0
                  ? t('notifications.empty')
                  : t('notifications.count', { count: notifications.length })}
              </SheetDescription>
            </div>
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={clearNotifications}
                title={t('notifications.clearAll')}
                aria-label={t('notifications.clearAll')}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-120px)] mt-6 pr-4">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Bell className="h-12 w-12 mb-4 opacity-20" />
              <p>{t('notifications.empty')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className="border rounded-lg p-4 space-y-2 hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {(() => {
                      const meta = TYPE_ICON[notification.type] ?? TYPE_ICON.info;
                      return (
                        <span className="mt-0.5 shrink-0">
                          <meta.Icon className={`h-4 w-4 ${meta.className}`} aria-hidden="true" />
                          <span className="sr-only">{t(`notifications.types.${notification.type}`)}</span>
                        </span>
                      );
                    })()}
                    <div className="flex-1 space-y-1">
                      <p className="font-medium leading-none">{notification.title}</p>
                      {notification.description && (
                        <p className="text-sm text-muted-foreground">{notification.description}</p>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {format(notification.timestamp, 'PPp')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
